/**
 * ダイアログの共通処理（FR-A11Y-01）。
 *
 * 役割（`role="dialog"`）・モーダルであること（`aria-modal`）・アクセシブルネーム・
 * 開いたときのフォーカス移動・Tab の閉じ込め・閉じたときの復帰を 1 か所にまとめる。
 * 面ごとに書くとどれかが必ず抜けるため、重なりを作る 3 つの面（ライトボックス・
 * 解錠フォーム・注釈ノート）が同じ手順を通るようにしている。
 *
 * Esc は「内側から 1 段ずつ閉じる」規約（`ui/shell.ts`）に合わせ、**重なりの最上段だけ**が
 * 引き取る。どれが最上段かはここが持つスタックが決め、Shell（サイドパネル）と
 * ライトボックスは {@link escapeClaimed} を見て手を出さない。
 *
 * ここを使うのは**閉じる手段を持つ面だけ**。閉じ方の無い面で焦点を閉じ込めると、
 * それ自体がキーボードトラップ（WCAG 2.1.2）になる（解錠フォームがその例で、
 * あちらは名前つきの `<form>` として扱い閉じ込めない）。
 */

/**
 * フォーカスを受け取れる要素。`tabindex="-1"` を全種別から外しているのは、
 * ロービング tabindex のツールバー（`ui/toolbar.ts`）を **1 つの止まり木**として
 * 扱うため（内側のボタン十数個が Tab の順路に並ばない）。
 */
const FOCUSABLE = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export interface DialogOptions {
  /** アクセシブルネーム。`labelledBy` を渡すならそちらが優先される。 */
  label?: string;
  /** 見出し要素の id（`aria-labelledby`）。 */
  labelledBy?: string;
  /** 開いたときに焦点を当てる要素。省略時は最初の操作子。 */
  initialFocus?: HTMLElement;
  /**
   * Esc を引き取るなら渡す。渡さない面は Esc を自分で扱う
   * （ライトボックスはサイドパネルより後に閉じる必要があり、順序が違う）。
   */
  onEscape?: () => void;
}

interface Entry {
  el: HTMLElement;
  onEscape: (() => void) | null;
}

const stack: Entry[] = [];
let detachEscape: (() => void) | null = null;

/**
 * Esc を引き取るモーダルが開いているか。Shell（サイドパネル）とライトボックスは
 * これが真なら Esc に手を出さない＝いちばん内側が閉じる。ライトボックス自身も
 * スタックには載るが Esc を引き取らないため、ここでは数えない。
 */
export function escapeClaimed(): boolean {
  return stack.some((entry) => entry.onEscape !== null);
}

/** el 配下（と、el 自身が焦点を取れるなら el）のフォーカス可能な要素。 */
function focusableIn(el: HTMLElement): HTMLElement[] {
  const found = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    // 隠されている入れ物（閉じたパネルなど）の中は順路に含めない
    (target) => target.closest('[hidden]') === null,
  );
  const tabIndex = el.getAttribute('tabindex');
  return tabIndex !== null && tabIndex !== '-1' ? [el, ...found] : found;
}

function installEscape(doc: Document): void {
  if (detachEscape) return;
  const onKeydown = (event: Event): void => {
    const keyboard = event as KeyboardEvent;
    if (keyboard.key !== 'Escape' || keyboard.defaultPrevented) return;
    const top = stack[stack.length - 1];
    if (!top?.onEscape) return;
    keyboard.preventDefault();
    top.onEscape();
  };
  doc.addEventListener('keydown', onKeydown, true);
  detachEscape = () => doc.removeEventListener('keydown', onKeydown, true);
}

/**
 * 要素をダイアログとして開く。戻り値を呼ぶと属性・閉じ込め・フォーカスを開く前の
 * 状態へ戻す（閉じるときと dispose のどちらから呼んでもよい冪等な後始末）。
 */
export function openDialog(el: HTMLElement, options: DialogOptions = {}): () => void {
  const doc = el.ownerDocument;
  const previous = doc.activeElement as HTMLElement | null;

  // 元の属性を控えてから上書きする（ライトボックスのルートは元から aria-label を持つ）
  const restore: [string, string | null][] = [];
  const set = (name: string, value: string): void => {
    restore.push([name, el.getAttribute(name)]);
    el.setAttribute(name, value);
  };
  set('role', 'dialog');
  set('aria-modal', 'true');
  if (options.labelledBy !== undefined) set('aria-labelledby', options.labelledBy);
  else if (options.label !== undefined) set('aria-label', options.label);

  const entry: Entry = { el, onEscape: options.onEscape ?? null };
  stack.push(entry);
  installEscape(doc);

  // Tab の閉じ込め。最上段だけが効く（下の段は素通しして二重に奪わない）。
  const onKeydown = (event: Event): void => {
    const keyboard = event as KeyboardEvent;
    if (keyboard.key !== 'Tab' || keyboard.defaultPrevented) return;
    if (stack[stack.length - 1] !== entry) return;

    const targets = focusableIn(el);
    const first = targets[0];
    const last = targets[targets.length - 1];
    if (!first || !last) {
      keyboard.preventDefault();
      return;
    }
    const active = doc.activeElement;
    const outside = active === null || !el.contains(active);
    if (keyboard.shiftKey && (active === first || outside)) {
      keyboard.preventDefault();
      last.focus();
    } else if (!keyboard.shiftKey && (active === last || outside)) {
      keyboard.preventDefault();
      first.focus();
    }
  };
  doc.addEventListener('keydown', onKeydown, true);

  (options.initialFocus ?? focusableIn(el)[0])?.focus();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    doc.removeEventListener('keydown', onKeydown, true);
    const index = stack.indexOf(entry);
    if (index >= 0) stack.splice(index, 1);
    if (stack.length === 0) {
      detachEscape?.();
      detachEscape = null;
    }
    for (const [name, value] of restore) {
      if (value === null) el.removeAttribute(name);
      else el.setAttribute(name, value);
    }
    previous?.focus();
  };
}
